import { BarChartOutlined, CloudDownloadOutlined } from '@ant-design/icons'
import type { Chart } from '@antv/g2'
import { useDownload } from '@web/react-common'
import { Button, Typography } from 'antd'
import React from 'react'

import { useGetStatisticsQuery } from '@/generated/graphql'

import s from '../kpi.module.less'
import { TimeRangeType } from './statistics.interface'

export const StatisticsPostingCount: React.FC<{
  rangeType: TimeRangeType
  range: {
    year?: number
    month?: number
    day?: number
    week?: number
  }
}> = ({ range, rangeType }) => {
  const barDesc = '发帖数'
  const title = '发帖数'
  const divId = 'postCountChartDiv'
  const [chart, setChart] = React.useState<Chart>()

  const ref = React.useRef<HTMLDivElement>(null)

  const { data } = useGetStatisticsQuery({
    skip: !range.year && !range.month && !range.day,
    variables: {
      withPostCount: true,
      input: {
        year: range.year,
        month: range.month,
        day: range.day,
      },
    },
  })

  const statistics = data?.statistics.postsCount || []

  React.useEffect(() => {
    import('../chart.component').then(({ default: Chart }) => {
      const newChart = new Chart({
        container: divId,
        autoFit: true,
        appendPadding: [0, 25, 0, 0],
      })
      newChart.animate(false)
      setChart(newChart)
    })
  }, [])

  React.useEffect(() => {
    if (statistics && chart) {
      chart.clear()
      chart.scale({
        [rangeType]: {
          range: [0, 1],
        },
        value: {
          alias: barDesc,
          sync: true,
          nice: true,
        },
        Value: {
          sync: true,
          nice: true,
        },
      })

      chart.axis(rangeType, {
        tickLine: {
          alignTick: true,
        },
      })

      const view = chart.createView()
      view.data(statistics)
      view.interval().position(['date', 'value']).style({ fillOpacity: 1 })

      chart.render()
    }
  }, [chart, statistics])

  const file = [['日期', title]].concat(
    statistics.length > 0
      ? statistics.map((value) => [value.date || value.yearMonth || value.year || '', value.value + ''])
      : [['', '']]
  )

  const fileName = `Tale${title} - ${range.year}${range.month ? `.${range.month}` : ''}${
    range.day ? `.${range.day}` : ''
  }.csv`

  const header = (title: string) => (
    <div style={{ display: 'flex', fontSize: 32, margin: '16px 0 8px 0', justifyContent: 'center' }}>
      <BarChartOutlined />
      <Typography.Title style={{ marginLeft: 8 }} level={3}>
        {title}
      </Typography.Title>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Button icon={<CloudDownloadOutlined />} type='link' onClick={() => useDownload(file, fileName)}>
          下载
        </Button>
      </div>
    </div>
  )

  return (
    <div className={s.chartContainer}>
      {header(title)}
      <div className={s.chartBox}>
        <div id={divId} className={s.chart} ref={ref} />
      </div>
    </div>
  )
}
